import { Button, Field, FieldLabel, Input } from "@forge/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  isRedirect,
  Link,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { useState } from "react";
import { api, unwrap } from "../lib/api";
import { loadSession } from "../lib/auth";
import { SITE_NAME } from "../lib/site";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): { reset?: "1" } =>
    search.reset === "1" ? { reset: "1" } : {},
  beforeLoad: async ({ context }) => {
    try {
      await loadSession(context.queryClient);
      throw redirect({ to: "/home" });
    } catch (error) {
      if (isRedirect(error)) throw error;
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { reset } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");

  const login = useMutation({
    mutationFn: async () => {
      if (challenge) {
        return unwrap(
          await api.auth.login["2fa"].post({ challenge, code: code.trim() }),
        );
      }
      return unwrap(await api.auth.login.post({ email, password }));
    },
    onSuccess: async (data) => {
      if ("twoFactor" in data && data.twoFactor) {
        setChallenge(data.challenge);
        return;
      }
      queryClient.clear();
      await router.invalidate();
      await navigate({ to: "/home" });
    },
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface p-4">
      <div className="w-full max-w-xs">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Link
            to="/"
            className="flex size-10 items-center justify-center rounded-surface bg-ember text-ink-inverse shadow-raised"
          >
            <Flame className="size-5" />
          </Link>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              {SITE_NAME}
            </h1>
            <p className="text-2xs text-ink-muted">
              A operação da sua loja no Mercado Livre
            </p>
          </div>
        </div>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            login.mutate();
          }}
        >
          <Field>
            <FieldLabel>E-mail</FieldLabel>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@empresa.com.br"
            />
          </Field>
          {!challenge ? (
            <>
          <Field>
            <div className="flex items-center justify-between gap-2">
              <FieldLabel>Senha</FieldLabel>
              <Link
                to="/forgot-password"
                className="text-2xs text-ink-muted underline-offset-2 hover:text-ink hover:underline"
              >
                Esqueci a senha
              </Link>
            </div>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </Field>
            </>
          ) : (
            <Field>
              <FieldLabel>Código de verificação</FieldLabel>
              <Input
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6 dígitos ou código reserva"
              />
            </Field>
          )}

          {reset === "1" && !login.isError && !challenge && (
            <p className="rounded-control border border-success/25 bg-success-subtle px-3 py-2 text-2xs text-success">
              Senha atualizada. Entre com a nova senha.
            </p>
          )}

          {login.isError && (
            <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
              {challenge
                ? "Código inválido. Tente de novo."
                : "E-mail ou senha inválidos. Verifique e tente novamente."}
            </p>
          )}

          <Button type="submit" size="lg" loading={login.isPending}>
            {challenge ? "Confirmar" : "Entrar"}
          </Button>
          {challenge && (
            <button
              type="button"
              className="text-2xs text-ink-muted underline-offset-2 hover:text-ink hover:underline"
              onClick={() => {
                setChallenge(null);
                setCode("");
              }}
            >
              Voltar
            </button>
          )}
        </form>

        <p className="mt-6 text-center text-2xs text-ink-muted">
          Ainda não tem conta?{" "}
          <Link to="/signup" className="text-ink underline-offset-2 hover:underline">
            Criar conta
          </Link>
        </p>
      </div>
    </div>
  );
}
